import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../utils/apiBase';

/**
 * Public landing page rendered when a user clicks the "Reset password"
 * button in their email. Reads `?token=...` from the URL, accepts a new
 * password, calls POST /api/auth/reset-password and on success the
 * backend issues a fresh session cookie + we refresh the AuthContext
 * so the user is auto-logged-in.
 *
 * Mounted at /auth/reset-password — short-circuited inside <AppContent>
 * before the regular section router runs.
 */
export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const { refresh } = useAuth();

  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const token = params?.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (!token) {
      setError(t('reset.invalidToken'));
      return;
    }
    if (!password || password.length < 6) {
      setError(t('reset.minLength'));
      return;
    }
    if (password !== confirm) {
      setError(t('reset.mismatch'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((d) => d?.msg).join(' ') : t('reset.genericError'));
        setError(msg);
        return;
      }
      // Fresh session cookie was set server-side — refresh context
      await refresh();
      setDone(true);
    } catch (err) {
      setError(err.message || t('reset.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const goHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-gray-100 p-7 sm:p-8"
        data-testid="reset-password-page"
      >
        <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
          {done ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : (
            <Lock className="w-6 h-6 text-white" />
          )}
        </div>

        <h1 className="text-xl font-bold text-center text-gray-900 mb-1">
          {done ? t('reset.successTitle') : t('reset.title')}
        </h1>
        <p className="text-sm text-center text-gray-500 mb-5 leading-relaxed">
          {done ? t('reset.successBody') : t('reset.subtitle')}
        </p>

        {done ? (
          <button
            onClick={goHome}
            data-testid="reset-password-go-home"
            className="w-full px-4 py-3 min-h-[44px] bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition"
          >
            {t('reset.continueCta')}
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-password-form">
            <div>
              <label htmlFor="reset-password-input" className="block text-xs font-medium text-gray-600 mb-1">
                {t('reset.newPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="reset-password-input"
                  data-testid="reset-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('reset.placeholder')}
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="reset-password-confirm" className="block text-xs font-medium text-gray-600 mb-1">
                {t('reset.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="reset-password-confirm"
                  data-testid="reset-password-confirm"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t('reset.confirmPlaceholder')}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-password-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit"
              className="w-full px-4 py-2.5 min-h-[44px] bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('reset.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
